import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { DataTable } from '@/components/ui/data-table';
import { StatusPill } from '@/components/ui/status-pill';
import { api } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { useAuth } from '@/components/AuthProvider';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';

export default function TrackingPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManageTokens = user?.role === 'admin' || user?.role === 'dispatcher';
  const [loadId, setLoadId] = useState('');
  const [lastLink, setLastLink] = useState('');

  const tokensQ = useQuery({
    queryKey: ['tracking-tokens'],
    queryFn: async () => {
      const r = await api('/api/v1/tracking/tokens');
      if (r.status !== 200) throw new Error('tokens');
      return r.data.tokens ?? [];
    },
  });

  const loadsQ = useQuery({
    queryKey: ['loads'],
    queryFn: async () => {
      const r = await api('/api/v1/loads');
      if (r.status !== 200) throw new Error('loads');
      return r.data.loads ?? [];
    },
  });

  const tokens = tokensQ.data ?? [];
  const loads = loadsQ.data ?? [];

  const sortTokens = (list) =>
    [...(list || [])].sort((a, b) => {
      // Active first; revoked at end. Newest within each group.
      const ar = Number(Boolean(a.revoked));
      const br = Number(Boolean(b.revoked));
      if (ar !== br) return ar - br;
      return String(b.createdAt ?? '').localeCompare(String(a.createdAt ?? ''));
    });

  const createMut = useMutation({
    mutationFn: async (body) => {
      const r = await api('/api/v1/tracking/tokens', { method: 'POST', body });
      if (r.status !== 201) throw new Error(String(r.data?.error ?? 'Failed'));
      return r.data;
    },
    onSuccess: (data) => {
      const path = `/portal/${data.token.token}`;
      const url = `${window.location.origin}${path}`;
      setLastLink(url);
      toast.success('Token created');
      if (data?.token) {
        qc.setQueryData(['tracking-tokens'], (prev) => {
          const list = Array.isArray(prev) ? prev.filter((t) => t.id !== data.token.id) : [];
          return sortTokens([data.token, ...list]);
        });
      }
    },
    onError: (err) => toast.error(err.message || 'Failed'),
  });

  const revokeMut = useMutation({
    mutationFn: async (id) => {
      const r = await api(`/api/v1/tracking/tokens/${id}/revoke`, {
        method: 'POST',
        body: {},
      });
      if (r.status < 200 || r.status >= 300) {
        throw new Error(String(r.data?.error ?? 'Revoke failed'));
      }
      return r.data;
    },
    onSuccess: (data, id) => {
      toast.success('Token revoked (load is unchanged)');
      qc.setQueryData(['tracking-tokens'], (prev) => {
        if (!Array.isArray(prev)) return prev;
        const next = prev.map((t) =>
          t.id === id ? { ...t, ...(data?.token || {}), revoked: true } : t
        );
        return sortTokens(next);
      });
    },
    onError: (err) => toast.error(err.message || 'Revoke failed'),
  });

  async function copyLink(url) {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.message(url);
    }
  }

  const selectedLoad = loads.find((l) => l.id === loadId);
  const activeTokenForLoad = tokens.find(
    (t) => t.loadId === loadId && !t.revoked && new Date(t.expiresAt).getTime() >= Date.now()
  );

  const timelineSteps = useMemo(() => {
    if (!selectedLoad) return [];
    const status = selectedLoad.status || 'draft';
    return [
      {
        label: 'Load created',
        detail: `#${selectedLoad.loadNumber}`,
        status: 'done',
      },
      {
        label: status.replace(/_/g, ' '),
        detail: 'Current load status',
        status:
          status === 'in_transit' || status === 'dispatched'
            ? 'live'
            : status === 'delivered' || status === 'closed'
              ? 'done'
              : 'pending',
      },
      {
        label: 'Customer portal',
        detail: lastLink ? 'Share link ready' : 'Create a token to share status',
        status: lastLink ? 'live' : 'pending',
      },
    ];
  }, [selectedLoad, lastLink]);

  const tableRows = sortTokens(tokens).map((t) => ({
    ...t,
    load: loads.find((l) => l.id === t.loadId)?.loadNumber ?? t.loadId?.slice?.(0, 8) ?? '—',
    status: t.revoked ? 'revoked' : 'active',
    expires: t.expiresAt,
  }));

  const columns = [
    { key: 'load', label: 'Load', className: 'font-mono' },
    {
      key: 'token',
      label: 'Token',
      className: 'font-mono text-xs',
      render: (v) => `${String(v).slice(0, 12)}…`,
    },
    { key: 'expires', label: 'Expires', render: (v) => formatDate(v) },
    { key: 'status', label: 'Status', render: (v) => <StatusPill status={v} /> },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, row) => {
        const url = `${window.location.origin}/portal/${row.token}`;
        return (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => copyLink(url)}>
              <Icons.Copy className="h-3 w-3 mr-1" />
              Copy
            </Button>
            {!row.revoked && canManageTokens ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => revokeMut.mutate(row.id)}
                disabled={revokeMut.isPending}
              >
                Revoke
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Tracking"
          description="Generate customer tracking links"
          icon="MapPinned"
          metrics={[
            { label: 'Tokens', value: tokens.length },
            {
              label: 'Active',
              value: tokens.filter((t) => !t.revoked).length,
            },
            {
              label: 'Revoked',
              value: tokens.filter((t) => t.revoked).length,
            },
            { label: 'Loads', value: loads.length },
          ]}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {canManageTokens ? (
            <Card>
              <CardHeader>
                <CardTitle>Generate Tracking Link</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Combobox
                  options={loads.map((l) => ({ value: l.id, label: l.loadNumber }))}
                  value={loadId}
                  onChange={setLoadId}
                  placeholder="Select load"
                />
                <Button
                  className="w-full"
                  onClick={() => {
                    if (!loadId) {
                      toast.error('Select a load');
                      return;
                    }
                    if (activeTokenForLoad) {
                      toast.error('Revoke the active token for this load first');
                      return;
                    }
                    createMut.mutate({ loadId });
                  }}
                  disabled={createMut.isPending || !!activeTokenForLoad}
                >
                  <Icons.Link className="h-4 w-4 mr-2" />
                  {activeTokenForLoad
                    ? 'Active token exists'
                    : createMut.isPending
                      ? 'Creating…'
                      : 'Create Token'}
                </Button>
                {lastLink ? (
                  <div className="space-y-2">
                    <p className="text-xs font-mono text-muted-foreground break-all">{lastLink}</p>
                    <Button variant="outline" size="sm" onClick={() => copyLink(lastLink)}>
                      Copy last link
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Tracking</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Select a load to preview status. Creating and revoking tokens requires a
                  dispatcher.
                </p>
                <div className="mt-4">
                  <Combobox
                    options={loads.map((l) => ({ value: l.id, label: l.loadNumber }))}
                    value={loadId}
                    onChange={setLoadId}
                    placeholder="Select load"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Live Status Preview</CardTitle>
            </CardHeader>
            <CardContent>
              {timelineSteps.length === 0 ? (
                <p className="text-sm text-muted-foreground">Select a load to preview status steps.</p>
              ) : (
                <div className="space-y-4">
                  {timelineSteps.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div
                        className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          step.status === 'done'
                            ? 'bg-green-500 text-white'
                            : step.status === 'live'
                              ? 'bg-primary text-white'
                              : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {step.status === 'done' ? (
                          <Icons.Check className="h-4 w-4" />
                        ) : step.status === 'live' ? (
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
                          </span>
                        ) : (
                          <Icons.Circle className="h-3 w-3" />
                        )}
                      </div>
                      <div className="flex-1 pt-1">
                        <p className="font-medium text-sm capitalize">{step.label}</p>
                        <p className="text-xs text-muted-foreground">{step.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {tokensQ.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading tokens…</p>
        ) : (
          <DataTable columns={columns} data={tableRows} emptyMessage="No tracking tokens yet" />
        )}
      </div>
);
}
